import { useEffect, useState, useSyncExternalStore } from "react"

const GMAPS_SCRIPT_ID = 'hr-google-maps-sdk'
// Page-global callback name the `loading=async` bootstrap invokes once the
// core library is ready. Fixed (not per-instance) so every widget on the
// page agrees on the one script tag and the one callback it requests.
const GMAPS_CALLBACK_NAME = '__hrGoogleMapsReady'

// Readiness is `google.maps.Map`, not the bare `google.maps` namespace.
//
// On the path we own — script tag with `loading=async` AND `callback=` — the
// callback only fires once the core library is in place. Measured against the
// live API: at `script.onload` the whole `google` namespace is still undefined
// (importLibrary included), and by the time the callback runs `Map`, `LatLng`,
// `LatLngBounds`, `event` and `OverlayView` are all present together. So on
// that path either sentinel would do.
//
// It matters on the POLLING fallback below, which watches a script tag some
// other code injected. There we observe whatever bootstrap that code used, and
// the `loading=async` bootstrap publishes `google.maps.importLibrary` before
// the core classes land — a bare-namespace check would flip true while
// `google.maps.Map` is still undefined, and GoogleMap.tsx constructs
// `new g.maps.Map(...)` the moment this hook returns true.
const isMapReady = () =>
  typeof window !== 'undefined' &&
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  typeof (window as any).google?.maps?.Map === 'function'

// Listener lists keyed by the actual <script> element: multiple hook
// instances on one page find the same element and share its listener list
// (one script tag, one callback, every instance resolves), while a fresh
// element — a real page load, or a script tag some OTHER code injected —
// starts with no tracked listeners and falls back to polling.
const listenersByScript = new WeakMap<HTMLScriptElement, Array<() => void>>()

// How long the polling fallback watches a script tag we did not create before
// giving up. Without a deadline it ran every 50ms for the life of the page —
// measured at 196 polls in 10s against an unreachable host, and it outlived the
// component, because the timer chain lives here rather than in the hook.
const GMAPS_POLL_TIMEOUT_MS = 15000
const GMAPS_POLL_INTERVAL_MS = 50

// A failed load is page-global, like the script tag itself. Widgets subscribe
// so they can say so instead of sitting on "Loading map…" forever.
let loadFailed = false
const failureSubscribers = new Set<() => void>()
const setLoadFailed = (failed: boolean) => {
  if (loadFailed === failed) return
  loadFailed = failed
  for (const notify of failureSubscribers) notify()
}

/**
 * True once a Google Maps load has definitively failed — the script errored, or
 * a foreign tag never produced the core library within the poll deadline.
 * Page-global and resets when a fresh attempt starts.
 */
export function useGoogleMapsLoadFailed(): boolean {
  return useSyncExternalStore(
    (notify) => { failureSubscribers.add(notify); return () => { failureSubscribers.delete(notify) } },
    () => loadFailed,
    () => false,
  )
}

function loadGoogleMaps(apiKey: string, onReady: () => void) {
  if (isMapReady()) { onReady(); return }

  const existing = document.getElementById(GMAPS_SCRIPT_ID) as HTMLScriptElement | null
  if (existing) {
    const listeners = listenersByScript.get(existing)
    if (listeners) {
      listeners.push(onReady)
      return
    }
    // A script tag exists that we didn't create (or lost track of) — no
    // callback to hook into, so poll for the Map class instead. Bounded: a tag
    // that never publishes the core library is a failure, not a reason to spin
    // a 20Hz timer for as long as the page is open.
    const deadline = Date.now() + GMAPS_POLL_TIMEOUT_MS
    const poll = () => {
      if (isMapReady()) { setLoadFailed(false); onReady(); return }
      if (Date.now() >= deadline) {
        console.error(
          `[hr] Google Maps: a foreign #${GMAPS_SCRIPT_ID} script did not expose google.maps.Map within ${GMAPS_POLL_TIMEOUT_MS}ms; giving up.`,
        )
        setLoadFailed(true)
        return
      }
      setTimeout(poll, GMAPS_POLL_INTERVAL_MS)
    }
    poll()
    return
  }

  const listeners = [onReady]
  const script = document.createElement('script')
  listenersByScript.set(script, listeners)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ;(window as any)[GMAPS_CALLBACK_NAME] = () => {
    setLoadFailed(false)
    for (const fn of listeners) fn()
  }
  script.id = GMAPS_SCRIPT_ID
  script.async = true
  script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=marker&v=weekly&loading=async&callback=${GMAPS_CALLBACK_NAME}`
  // Blocked by an ad blocker or a consent gate, 404, offline: the callback can
  // never fire, so without this the hook stays false and the map sits on
  // "Loading map…" for the life of the page.
  //
  // The dead tag is deliberately LEFT in the DOM. Removing it would let every
  // later mount re-inject and re-fail against a host that is not coming back,
  // and a second mount that finds it now takes the polling branch, which is
  // bounded and reports the same failure.
  script.onerror = () => {
    console.error('[hr] Google Maps: the API script failed to load (blocked, offline, or an unreachable host).')
    setLoadFailed(true)
  }
  // A retry supersedes any previous failure.
  setLoadFailed(false)
  document.head.appendChild(script)
}

/**
 * Dynamically loads the Google Maps JS API and returns true once ready.
 * Deduplicates the script tag across widgets (the key is fixed per page).
 * The key is referrer-restricted client-side config — never a server secret.
 */
export function useGoogleMaps(apiKey?: string): boolean {
  const [loaded, setLoaded] = useState(() => isMapReady())

  useEffect(() => {
    if (loaded || !apiKey) return
    if (isMapReady()) { setLoaded(true); return }

    let cancelled = false
    loadGoogleMaps(apiKey, () => { if (!cancelled) setLoaded(true) })

    return () => { cancelled = true }
  }, [loaded, apiKey])

  return loaded
}
